import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Type, Palette, Layers, Volume2, Shirt } from 'lucide-react';

const types = [
  { icon: Type, name: 'Word Mark', description: 'Covers the text itself -- your brand name, slogan, or tagline -- in any font or style.', example: '"NIKE", "JUST DO IT"' },
  { icon: Palette, name: 'Design Mark', description: 'Protects a specific logo, symbol, or graphic element tied to your brand identity.', example: 'Apple logo, Nike swoosh' },
  { icon: Layers, name: 'Composite Mark', description: 'Shields the combination of your text and design as a unified trademark.', example: 'Starbucks name + mermaid' },
  { icon: Volume2, name: 'Sound Mark', description: 'Secures a distinctive audio signature that consumers associate with your brand.', example: 'NBC chimes, Intel bong' },
  { icon: Shirt, name: 'Trade Dress', description: 'Guards the overall visual appearance or packaging that signals your product source.', example: 'Coca-Cola bottle shape' },
];

const TrademarkTypesSection = () => {
  const header = useScrollAnimation({ threshold: 0.15 });
  return (
    <section data-testid="trademark-types-section" className="py-24 md:py-32 bg-[#093336]/20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div ref={header.ref} className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
          <span className="inline-block bg-[#A5BF23]/10 text-[#A5BF23] px-4 py-1.5 rounded-full text-xs font-bold tracking-[0.2em] mb-4">BRAND NAME PROTECTION</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F0F4F4] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Ways to Protect Your Brand Name</h2>
          <p className="text-base text-[#A0B2B2] leading-relaxed">Your brand name can be protected in several ways through the USPTO. Understanding the right type of filing ensures your name gets maximum legal coverage.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {types.slice(0, 3).map((type, i) => <TypeCard key={i} type={type} index={i} />)}
        </div>
        <div className="grid sm:grid-cols-2 gap-6 mt-6 max-w-2xl mx-auto lg:max-w-none lg:grid-cols-2 lg:px-[16.7%]">
          {types.slice(3).map((type, i) => <TypeCard key={i + 3} type={type} index={i + 3} />)}
        </div>
      </div>
    </section>
  );
};

const TypeCard = ({ type, index }) => {
  const card = useScrollAnimation({ threshold: 0.1 });
  const Icon = type.icon;
  return (
    <div ref={card.ref} data-testid={`trademark-type-${index}`}
      className={`group bg-[#093336]/60 backdrop-blur-sm border border-white/5 rounded-2xl p-7 transition-all duration-300 hover:border-[#A5BF23]/40 hover:bg-[#093336]/80 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(165,191,35,0.08)] scroll-fade-up scroll-delay-${(index % 3) + 1} ${card.isVisible ? 'scroll-visible' : ''}`}>
      <div className="w-12 h-12 rounded-xl bg-[#A5BF23]/10 flex items-center justify-center mb-5 group-hover:bg-[#A5BF23]/20 transition-colors duration-300">
        <Icon className="w-6 h-6 text-[#A5BF23]" strokeWidth={1.5} />
      </div>
      <h3 className="text-lg font-bold text-[#F0F4F4] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{type.name}</h3>
      <p className="text-sm text-[#A0B2B2] leading-relaxed mb-3">{type.description}</p>
      <span className="text-xs text-[#A5BF23] font-medium bg-[#A5BF23]/5 px-3 py-1 rounded-full inline-block">{type.example}</span>
    </div>
  );
};

export default TrademarkTypesSection;
